/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React from 'react'

import {
  EuiFormRow,
  EuiFieldText,
  EuiButtonIcon,
  EuiFlexGroup,
  EuiFlexItem,
  EuiSpacer,
  EuiToolTip,
} from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import type { TrafficFilterRule } from '@modules/cloud-api/v1/types'

type Props = {
  rules: TrafficFilterRule[]
  onChange: (rules: TrafficFilterRule[]) => void
}

const RemoteClusterField: React.FunctionComponent<Props> = ({ rules, onChange }) => {
  // Initialize with at least one empty rule
  const rulesList =
    rules.length > 0 ? rules : [{ remote_cluster_org_id: '', remote_cluster_id: '' }]

  const onChangeRemoteClusterOrgId = (index: number, value: string) => {
    const updatedRules = [...rulesList]

    if (!updatedRules[index]) {
      updatedRules[index] = {}
    }

    updatedRules[index].remote_cluster_org_id = value
    onChange(updatedRules)
  }

  const onChangeRemoteClusterId = (index: number, value: string) => {
    const updatedRules = [...rulesList]

    if (!updatedRules[index]) {
      updatedRules[index] = {}
    }

    updatedRules[index].remote_cluster_id = value
    onChange(updatedRules)
  }

  const onAddRemoteCluster = () => {
    const updatedRules = [...rulesList, { remote_cluster_org_id: '', remote_cluster_id: '' }]
    onChange(updatedRules)
  }

  const onRemoveRemoteCluster = (index: number) => {
    const updatedRules = [...rulesList]
    updatedRules.splice(index, 1)

    // Ensure there's always at least one rule
    if (updatedRules.length === 0) {
      updatedRules.push({ remote_cluster_org_id: '', remote_cluster_id: '' })
    }

    onChange(updatedRules)
  }

  return (
    <React.Fragment>
      <EuiSpacer size='m' />
      {rulesList.map((rule, i) => {
        const rowContent = (
          <React.Fragment key={i}>
            {i > 0 && <EuiSpacer size='m' />}

            <EuiFlexGroup gutterSize='m' alignItems='flexStart'>
              <EuiFlexItem grow={true}>
                <EuiFormRow
                  fullWidth={true}
                  label={
                    <EuiFlexGroup gutterSize='s' alignItems='center'>
                      <EuiFlexItem grow={false}>
                        <FormattedMessage
                          id='remoteClusterField.orgId.label'
                          defaultMessage='Organization ID {index}'
                          values={{ index: rulesList.length > 1 ? i + 1 : '' }}
                        />
                      </EuiFlexItem>
                      {i === rulesList.length - 1 && (
                        <EuiFlexItem grow={false}>
                          <EuiToolTip
                            content={
                              <FormattedMessage
                                id='remoteClusterField.orgId.labelAppend'
                                defaultMessage='You can find the Organization ID by going to the Organization page in the top right menu.'
                              />
                            }
                          >
                            <EuiButtonIcon
                              aria-label='How do I find my Organization ID?'
                              iconType='questionInCircle'
                              color='primary'
                              // pull back the extra height this takes up from the usual label text line-height
                              css={{ marginTop: '-4px', marginBottom: '-4px' }}
                            />
                          </EuiToolTip>
                        </EuiFlexItem>
                      )}
                    </EuiFlexGroup>
                  }
                >
                  <EuiFieldText
                    value={rule.remote_cluster_org_id || ''}
                    onChange={(e) => onChangeRemoteClusterOrgId(i, e.target.value)}
                    fullWidth={true}
                  />
                </EuiFormRow>
              </EuiFlexItem>
              <EuiFlexItem grow={true}>
                <EuiFormRow
                  fullWidth={true}
                  label={
                    <EuiFlexGroup gutterSize='s' alignItems='center'>
                      <EuiFlexItem grow={false}>
                        <FormattedMessage
                          id='remoteClusterField.clusterId.label'
                          defaultMessage='Elasticsearch ID {index}'
                          values={{ index: rulesList.length > 1 ? i + 1 : '' }}
                        />
                      </EuiFlexItem>
                      {i === rulesList.length - 1 && (
                        <EuiFlexItem grow={false}>
                          <EuiToolTip
                            content={
                              <FormattedMessage
                                id='remoteClusterField.clusterId.labelAppend'
                                defaultMessage='You can find the Elasticsearch ID by going to the Elasticsearch page of a deployment and hitting the "Copy cluster ID" button.'
                              />
                            }
                          >
                            <EuiButtonIcon
                              aria-label='How do I find my Elasticsearch ID?'
                              iconType='questionInCircle'
                              color='primary'
                              // pull back the extra height this takes up from the usual label text line-height
                              css={{ marginTop: '-4px', marginBottom: '-4px' }}
                            />
                          </EuiToolTip>
                        </EuiFlexItem>
                      )}
                    </EuiFlexGroup>
                  }
                >
                  <EuiFieldText
                    value={rule.remote_cluster_id || ''}
                    onChange={(e) => onChangeRemoteClusterId(i, e.target.value)}
                    fullWidth={true}
                  />
                </EuiFormRow>
              </EuiFlexItem>
              <EuiFlexItem grow={false}>
                <div style={{ marginTop: '28px' }}>
                  <EuiFlexGroup gutterSize='s' alignItems='flexStart'>
                    <EuiFlexItem grow={false}>
                      <EuiButtonIcon
                        aria-label='Remove remote cluster'
                        iconType='minusInCircleFilled'
                        color='primary'
                        onClick={() => onRemoveRemoteCluster(i)}
                        disabled={rulesList.length === 1}
                      />
                    </EuiFlexItem>
                    <EuiFlexItem grow={false}>
                      {i === rulesList.length - 1 ? (
                        <EuiButtonIcon
                          aria-label='Add remote cluster'
                          iconType='plusInCircleFilled'
                          color='primary'
                          onClick={onAddRemoteCluster}
                        />
                      ) : (
                        <div style={{ width: '24px' }} />
                      )}
                    </EuiFlexItem>
                  </EuiFlexGroup>
                </div>
              </EuiFlexItem>
            </EuiFlexGroup>
          </React.Fragment>
        )

        if (i === rulesList.length - 1) {
          return (
            <EuiFormRow
              key={i}
              fullWidth={true}
              helpText={
                <div style={{ maxWidth: '400px' }}>
                  <FormattedMessage
                    id='remoteClusterField.description'
                    defaultMessage='Fill in either one or both of the fields below, but keep in mind that the Organization ID takes precedence.'
                  />
                </div>
              }
            >
              {rowContent}
            </EuiFormRow>
          )
        }

        return rowContent
      })}
    </React.Fragment>
  )
}

export default RemoteClusterField
